// Horizontal scrollers (on-the-build strip, Night Shift wall):
// arrow buttons + mouse drag with momentum. Touch uses native scrolling.

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function bindScroller(root) {
  if (root.dataset.nrScrollerReady) return;
  root.dataset.nrScrollerReady = '1';

  const track = root.querySelector('[data-nr-track]');
  if (!track) return;
  const prev = root.querySelector('[data-nr-prev]');
  const next = root.querySelector('[data-nr-next]');
  // Window listeners are dropped when the theme editor unloads this section
  const ac = new AbortController();
  const { signal } = ac;
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      if (event.target.contains(root)) ac.abort();
    },
    { signal }
  );

  const step = () => {
    const item = track.firstElementChild;
    return item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
  };
  const behavior = reduceMotion.matches ? 'auto' : 'smooth';

  // aria-disabled (not disabled) so keyboard focus stays on the arrow at either end
  const go = (btn, dir) => btn?.addEventListener('click', () => {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    track.scrollBy({ left: dir * step(), behavior });
  });
  go(prev, -1);
  go(next, 1);

  const syncArrows = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    prev?.setAttribute('aria-disabled', String(track.scrollLeft <= 2));
    next?.setAttribute('aria-disabled', String(track.scrollLeft >= max));
  };
  track.addEventListener('scroll', syncArrows, { passive: true });
  // First read (and any resize) in a ResizeObserver callback, after layout: reading sizes at bind time forced one
  const sized = new ResizeObserver(syncArrows);
  sized.observe(track);
  signal.addEventListener('abort', () => sized.disconnect());

  // Mouse drag with momentum
  let dragging = false;
  let moved = 0;
  let startX = 0;
  let startLeft = 0;
  let lastX = 0;
  let lastT = 0;
  let velocity = 0;
  let glide = 0;

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    cancelAnimationFrame(glide);
    dragging = true;
    moved = 0;
    startX = lastX = event.clientX;
    lastT = performance.now();
    startLeft = track.scrollLeft;
    velocity = 0;
    track.classList.add('is-dragging');
  });

  window.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const now = performance.now();
    const dx = event.clientX - startX;
    moved = Math.max(moved, Math.abs(dx));
    if (moved > 4 && !track.hasPointerCapture?.(event.pointerId)) track.setPointerCapture?.(event.pointerId);
    track.scrollLeft = startLeft - dx;
    velocity = (event.clientX - lastX) / Math.max(now - lastT, 1);
    lastX = event.clientX;
    lastT = now;
  }, { signal });

  const end = () => {
    if (!dragging) return;
    dragging = false;
    // Mouse held still before release: no fling
    if (performance.now() - lastT > 80) velocity = 0;
    if (reduceMotion.matches || Math.abs(velocity) < 0.05) {
      track.classList.remove('is-dragging');
      return;
    }
    let v = velocity * 16;
    const tick = () => {
      track.scrollLeft -= v;
      v *= 0.94;
      if (Math.abs(v) > 0.5) glide = requestAnimationFrame(tick);
      else track.classList.remove('is-dragging');
    };
    glide = requestAnimationFrame(tick);
  };
  window.addEventListener('pointerup', end, { signal });
  window.addEventListener('pointercancel', end, { signal });

  // A drag shouldn't count as a click on a link inside the track
  track.addEventListener(
    'click',
    (event) => {
      if (moved > 6) {
        event.preventDefault();
        event.stopPropagation();
        moved = 0;
      }
    },
    true
  );
  track.addEventListener('dragstart', (event) => event.preventDefault());
}

function init(root = document) {
  root.querySelectorAll('[data-nr-scroller]').forEach(bindScroller);
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
